import {gql} from '@apollo/client';
import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import {DocumentNode} from 'graphql';
import React, {useState} from 'react';

import * as analytics from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {IDENTITY_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {USER_SUMMARY_FIELDS_FRAGMENT} from 'parties/users/UserSummaryFragment';
import useOnMount from 'ui-library/hooks/useOnMount';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import * as nyc from 'ui-library/NYC';

import {UserPageQuery} from './__generated/UserPageQuery';
import ResetMfaModal from './ResetMfaModal';

type UserPageQueryUser = ExtractByTypename<UserPageQuery['user'], 'UserNode'>;

export const USER_BASIC_SETTINGS_TAB_USER_FRAGMENT: DocumentNode = gql`
  fragment UserBasicSettingsTabUser on UserNode {
    ...UserSummaryFields
    mfaEnabled
    activeWarehouseLogins {
      id
      username
      label
      createdAt
    }
    organizations {
      id
      name
      handle
      urlIdentifier
    }
    teams {
      id
      name
      handle
    }
    apiKey {
      createdAt
    }
  }
  ${USER_SUMMARY_FIELDS_FRAGMENT}
`;

export interface UserBasicSettingsTabProps {
  user: UserPageQueryUser;
  canResetUserMfa: boolean;
}

export function UserAttribute({label, value}: {label: string; value: string}): JSX.Element {
  return (
    <div className="u-small-padding-top u-small-padding-left">
      <nyc.Row>
        <strong>{label}</strong>
      </nyc.Row>
      <nyc.Row>{value}</nyc.Row>
    </div>
  );
}

export default function UserBasicSettingsTab(props: UserBasicSettingsTabProps): JSX.Element {
  const [mfaEnabled, setMfaEnabled] = useState(props.user.mfaEnabled);
  const [resetModalOpen, setResetModalOpen] = useState(false);

  useOnMount(() => {
    analytics.trackEvent(IDENTITY_ANALYTICS_ACTIONS.OPENED_PARTY_BASIC_SETTINGS_TAB, {
      objectType: 'user',
      objectId: props.user.id,
    });
  });

  const attributes: {[key: string]: string} = {
    Name: props.user.name,
    Username: props.user.handle,
    Email: props.user.email,
  };
  return (
    <>
      <nyc.Container size="lg" className={css(styles.container)}>
        <nyc.Row>
          <nyc.Col lg={1} />
          <nyc.Col lg={21}>
            {Object.entries(attributes).map(([label, value]: [string, string]) => (
              <UserAttribute key={label} label={label} value={value} />
            ))}
            {BenchlingConfig.AUTH_ENABLE_MFA_FEATURE && (
              <div className="u-small-padding-top u-small-padding-left">
                <nyc.Row>
                  <strong>MFA status</strong>
                </nyc.Row>
                <nyc.Row>
                  {mfaEnabled ? (
                    <span className={css(styles.mfaStatus)}>
                      <BenchlingIcon icon="check-circle" className={css(styles.mfaEnabledIcon)} />
                      <span>Enabled</span>
                      {props.canResetUserMfa && (
                        <button
                          type="button"
                          className={css(styles.resetLink)}
                          onClick={() => {
                            setResetModalOpen(true);
                          }}
                        >
                          Reset
                        </button>
                      )}
                    </span>
                  ) : (
                    <span className="u-gray-darker">Not enabled</span>
                  )}
                </nyc.Row>
              </div>
            )}
          </nyc.Col>
        </nyc.Row>
      </nyc.Container>
      {resetModalOpen && (
        <ResetMfaModal
          userName={props.user.name}
          userEmail={props.user.email}
          userApiId={props.user.id}
          onClose={() => {
            setResetModalOpen(false);
          }}
          onReset={() => {
            setMfaEnabled(false);
            setResetModalOpen(false);
          }}
        />
      )}
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingTop: 20,
    fontSize: 13,
  },
  mfaStatus: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
  },
  mfaEnabledIcon: {
    color: Color.GREEN_500,
  },
  resetLink: {
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    color: Color.BLUE_400,
    fontSize: 'inherit',
    marginLeft: 4,
  },
});
